import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, ImagePlus, Loader2, UserPlus, CalendarPlus, ArrowLeft, Clock, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, API, errMsg, getToken } from "@/lib/api";

export const DEVICE_KEY = "ms_wa_device";
const WHO = { cliente: "Cliente", dipendente: "Dipendente", contatto: "Contatto", nuovo: "Nuovo contatto" };
const STATUS = { queued: "In coda", sent: "Inviato", delivered: "Consegnato", read: "Letto", failed: "Non inviato", undelivered: "Non consegnato" };
const when = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const mediaUrl = (m, i) => {
  const dev = window.location.pathname.startsWith("/whatsapp") && localStorage.getItem(DEVICE_KEY);
  return `${API}/inbox/media/${m.id}/${i}?auth=${dev ? `dev:${dev}` : getToken() || ""}`;
};

function ThreadList({ threads, active, onPick }) {
  return (
    <div className="divide-y overflow-y-auto" data-testid="wa-thread-list">
      {threads.map((t, i) => (
        <button key={t.phone} onClick={() => onPick(t)} data-testid={`wa-thread-${i}`}
          className={`block w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 ${active === t.phone ? "bg-[#a2ca6b]/15" : ""}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="truncate font-semibold">{t.who.name}</p>
            <span className="shrink-0 text-[11px] text-slate-400">{t.last_at && when(t.last_at)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-xs text-slate-500">{t.last_dir === "out" ? "Tu: " : ""}{t.last_text}</p>
            {t.unread > 0 && <span className="rounded-full bg-[#25D366] px-1.5 text-[11px] font-bold text-white" data-testid={`wa-thread-unread-${i}`}>{t.unread}</span>}
          </div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">{WHO[t.who.type]}{t.who.code ? ` · ${t.who.code}` : ""} · +{t.phone}</p>
        </button>
      ))}
      {!threads.length && <p className="p-6 text-center text-sm text-slate-500" data-testid="wa-empty">Nessuna conversazione. I messaggi che arrivano al numero aziendale compariranno qui.</p>}
    </div>
  );
}

function Bubble({ m }) {
  const out = m.dir === "out";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${out ? "bg-[#d9fdd3]" : "bg-white"}`}>
        {m.media.map((x) => x.type.startsWith("image/")
          ? <a key={x.i} href={mediaUrl(m, x.i)} target="_blank" rel="noreferrer"><img src={mediaUrl(m, x.i)} alt="" className="mb-1 max-h-60 rounded-lg" /></a>
          : <a key={x.i} href={mediaUrl(m, x.i)} target="_blank" rel="noreferrer" className="block underline">Allegato</a>)}
        {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
        <p className="mt-0.5 text-right text-[10px] text-slate-500">{out && m.by ? `${m.by} · ` : ""}{when(m.at)}{out && m.status ? ` · ${STATUS[m.status] || m.status}` : ""}</p>
      </div>
    </div>
  );
}

function Composer({ thread, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const send = async (file) => {
    if (!text.trim() && !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      await api.post(`/inbox/threads/${thread.phone}/send`, fd);
      setText("");
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!thread.window_open) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="space-y-2 border-t bg-white p-3">
        <p className="flex items-center gap-2 text-xs text-amber-800" data-testid="wa-window-closed"><Clock className="h-4 w-4" /> Oltre le 24 ore: il testo verrà inviato dentro il messaggio modello approvato di Madonna Store (solo testo).</p>
        <div className="flex items-center gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi il motivo del contatto" className="rounded-full" data-testid="wa-message-input" />
          <Button type="submit" size="icon" className="shrink-0 rounded-full bg-[#25D366] hover:bg-[#1ebe5a]" disabled={busy} data-testid="wa-send-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </form>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t bg-white p-3">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) send(f); }} />
      <Button type="button" variant="ghost" size="icon" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="wa-attach-button"><ImagePlus className="h-5 w-5" /></Button>
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio" className="rounded-full" data-testid="wa-message-input" />
      <Button type="submit" size="icon" className="shrink-0 rounded-full bg-[#25D366] hover:bg-[#1ebe5a]" disabled={busy} data-testid="wa-send-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
    </form>
  );
}

function NewChat({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("+39 ");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/inbox/new", { phone, text });
      toast.success("Messaggio inviato");
      setOpen(false);
      setText("");
      setPhone("+39 ");
      onCreated(data.phone);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return <div className="border-b p-3"><Button onClick={() => setOpen(true)} className="w-full rounded-full bg-[#25D366] hover:bg-[#1ebe5a]" data-testid="wa-new-chat"><MessageSquarePlus className="h-4 w-4" /> Nuovo messaggio</Button></div>;
  }
  return (
    <form onSubmit={send} className="space-y-2 border-b bg-slate-50 p-3" data-testid="wa-new-chat-form">
      <Input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" autoFocus data-testid="wa-new-phone" />
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Messaggio" data-testid="wa-new-text" />
      <p className="text-[11px] text-slate-500">Ai numeri che non vi hanno scritto nelle ultime 24 ore il testo arriva dentro il messaggio modello approvato di Madonna Store.</p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setOpen(false)}>Annulla</Button>
        <Button type="submit" disabled={busy || !text.trim()} className="flex-1 rounded-full bg-[#25D366] hover:bg-[#1ebe5a]" data-testid="wa-new-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Invia</Button>
      </div>
    </form>
  );
}

function ThreadHeader({ t, device, onBack, onChanged }) {
  const nav = useNavigate();
  const saveLead = async () => {
    const full = window.prompt("Nome e cognome del contatto", t.profile_name || "");
    if (!full) return;
    const [first, ...rest] = full.trim().split(" ");
    try {
      await api.post(`/inbox/threads/${t.phone}/lead`, { first_name: first, last_name: rest.join(" ") || "-" });
      toast.success("Contatto salvato");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex items-center justify-between gap-2 border-b bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onBack}><ArrowLeft className="h-5 w-5" /></Button>
        <div>
          <p className="font-semibold" data-testid="wa-thread-title">{t.who.name}</p>
          <p className="text-xs text-slate-500">{WHO[t.who.type]} · +{t.phone}</p>
        </div>
      </div>
      {t.who.type === "nuovo" && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={saveLead} data-testid="wa-save-lead"><UserPlus className="h-4 w-4" /> Salva contatto</Button>
          {!device && <Button size="sm" className="rounded-full" onClick={() => nav(`/admin/nuovo?phone=%2B${t.phone}&name=${encodeURIComponent(t.profile_name || "")}`)} data-testid="wa-new-job"><CalendarPlus className="h-4 w-4" /> Nuovo appuntamento</Button>}
        </div>
      )}
    </div>
  );
}

export function WhatsAppInbox({ device = false }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const endRef = useRef(null);
  const loadThreads = useCallback(() => api.get("/inbox/threads").then((r) => setThreads(r.data)).catch(() => {}), []);
  const loadMsgs = useCallback(() => {
    if (active) api.get(`/inbox/threads/${active}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  }, [active]);
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 4000); return () => clearInterval(t); }, [loadThreads]);
  useEffect(() => { setMsgs([]); loadMsgs(); const t = setInterval(loadMsgs, 3000); return () => clearInterval(t); }, [loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);
  const thread = threads.find((t) => t.phone === active);
  return (
    <div className="grid h-[calc(100vh-11rem)] min-h-[480px] overflow-hidden rounded-2xl border bg-white lg:grid-cols-[340px_1fr]" data-testid="wa-inbox">
      <div className={`${active ? "hidden lg:block" : "block"} overflow-y-auto border-r`}>
        <NewChat onCreated={(p) => { loadThreads(); setActive(p); }} />
        <ThreadList threads={threads} active={active} onPick={(t) => setActive(t.phone)} />
      </div>
      {thread ? (
        <div className="flex min-h-0 flex-col bg-[#efeae2]">
          <ThreadHeader t={thread} device={device} onBack={() => setActive(null)} onChanged={loadThreads} />
          <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="wa-messages">
            {msgs.map((m) => <Bubble key={m.id} m={m} />)}
            <div ref={endRef} />
          </div>
          <Composer thread={thread} onSent={() => { loadMsgs(); loadThreads(); }} />
        </div>
      ) : <div className="hidden items-center justify-center text-sm text-slate-500 lg:flex">Seleziona una conversazione</div>}
    </div>
  );
}
